import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { map, tap } from "rxjs/operators";
import { ApiService } from "./api.service";
import { LoginStateService } from "./login-state.service";
import { Router } from "@angular/router";
import { v4 as uuid } from "uuid";
import { environment } from "../../environments/environment";
import { Role } from "../classes/role";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  public role: Role = new Role();
  private readonly ACCESS_TOKEN = "ACCESS_TOKEN";
  private readonly REFRESH_TOKEN = "REFRESH_TOKEN";
  private readonly STATE = "STATE";
  private readonly CODE_VERIFIER = "CODE_VERIFIER";

  constructor(
    private http: HttpClient,
    private api: ApiService,
    private loginState: LoginStateService,
    private router: Router
  ) {}

  private getAuthOptions() {
    let headers: any = new HttpHeaders().append(
      "Content-Type",
      "application/x-www-form-urlencoded"
    );
    return { headers: headers };
  }

  public storeAccessToken(token: string) {
    localStorage.setItem(this.ACCESS_TOKEN, token);
  }

  public getAccessToken(): string {
    return localStorage.getItem(this.ACCESS_TOKEN);
  }

  public storeRefreshToken(token: string) {
    localStorage.setItem(this.REFRESH_TOKEN, token);
  }

  public getRefreshToken(): string {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  public storeState(state: string) {
    localStorage.setItem(this.STATE, state);
  }

  public getState(): string {
    return localStorage.getItem(this.STATE);
  }

  private getAuthUri(
    state: string,
    code_challenge: string,
    code_challenge_method: string
  ): string {
    return `${environment.ssoUrl}/authorize?
        state=${encodeURIComponent(state)}&
        scope=openid&
        client_id=${encodeURIComponent(environment.clientId)}&
        redirect_uri=${encodeURIComponent(environment.redirectUrl)}&
        response_type=code&
        token_content_type=jwt&
        code_challenge=${encodeURIComponent(code_challenge)}&
        code_challenge_method=${encodeURIComponent(code_challenge_method)}
      `.replace(/\s+/g, "");
  }

  public refreshAccessToken() {
    let params = `refresh_token=${this.getRefreshToken()}&redirect_uri=${
      environment.redirectUrl
    }&client_id=${
      environment.clientId
    }&grant_type=refresh_token&token_content_type=jwt`;
    return this.http
      .post<any>(
        `${environment.ssoUrl}/token`,
        new Blob([params], { type: "application/json" }),
        this.getAuthOptions()
      )
      .pipe(
        tap((response) => {
          if (response && response.id_token) {
            this.storeAccessToken(response.id_token);
            this.loginState.toggleLoggedIn(true);
          }
          if (response && response.refresh_token) {
            this.storeRefreshToken(response.refresh_token);
          }
        })
      );
  }

  public requestAccessToken(code: string, redirect?: string): Promise<any> {
    const code_verifier = localStorage.getItem(this.CODE_VERIFIER);
    if (!code_verifier) {
      return Promise.reject(
        new Error(
          "PKCE code_verifier missing from storage. Start auth flow again."
        )
      );
    }

    const params = `
      code=${encodeURIComponent(code)}&
      redirect_uri=${encodeURIComponent(environment.redirectUrl)}&
      client_id=${encodeURIComponent(environment.clientId)}&
      grant_type=authorization_code&
      token_content_type=jwt&
      code_verifier=${encodeURIComponent(code_verifier)}
    `.replace(/\s+/g, "");

    return this.http
      .post<any>(
        `${environment.ssoUrl}/token`,
        new Blob([params], { type: "application/json" }),
        this.getAuthOptions()
      )
      .toPromise()
      .then((response) => {
        if (response && response.id_token) {
          this.storeAccessToken(response.id_token);
          this.loginState.toggleLoggedIn(true);
        } else this.loginState.toggleLoggedIn(false);

        if (response && response.refresh_token)
          this.storeRefreshToken(response.refresh_token);
        if (redirect && redirect.indexOf(window.location.origin) !== -1) {
          this.router.navigateByUrl(redirect.split(window.location.origin)[1]);
        }
        return response;
      });
  }

  public async requestAuthCode(redirect?: string) {
    let state = uuid();
    let stateObj = {
      id: state,
      application: this.api.APPLICATION_BROKER,
      platform: this.api.PLATFORM,
      redirect: redirect,
    };
    let encoded = btoa(JSON.stringify(stateObj));
    this.storeState(encoded);

    // Generate and store a PKCE code_verifier, then compute the code_challenge
    const code_verifier = this.generateCodeVerifier();
    // generateCodeVerifier already stores the verifier in localStorage
    const code_challenge = await this.generateCodeChallenge(code_verifier);
    const code_challenge_method = "S256";

    window.location.href = this.getAuthUri(
      encoded,
      code_challenge,
      code_challenge_method
    );
  }

  // Helper: base64url encode an ArrayBuffer
  private base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const b64 = btoa(binary);
    // base64url: replace +/ with -_, remove padding
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  // Compute the code_challenge from a code_verifier using SHA-256 and base64url encoding
  private async generateCodeChallenge(code_verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(code_verifier);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    return this.base64UrlEncode(digest);
  }

  // Generate a cryptographically-random code_verifier (PKCE) and store it
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    // Convert to base64url
    let binary = "";
    for (let i = 0; i < array.byteLength; i++) {
      binary += String.fromCharCode(array[i]);
    }
    const b64 = btoa(binary);
    const code_verifier = b64
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    localStorage.setItem(this.CODE_VERIFIER, code_verifier);
    return code_verifier;
  }

  public logout() {
    this.storeAccessToken(null);
    this.storeRefreshToken(null);
    this.loginState.toggleLoggedIn(false);
    this.router.navigateByUrl("/start");
  }

  public validateSession() {
    let data = {
      application: this.api.APPLICATION_BROKER,
      platform: this.api.PLATFORM,
    };
    return this.http.post<any>(this.api.VERIFY_SESSION, data).pipe(
      map((response) => {
        this.role = new Role(response.role);
        return response;
      })
    );
  }
}
